import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

const NativeProblemSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
});

type NativeProblemFormValues = z.infer<typeof NativeProblemSchema>;

interface NativeProblemFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const NativeProblemForm: React.FC<NativeProblemFormProps> = ({ onSubmit, onClose, isOpen }) => {
  if (!isOpen) return null;

  const handleSubmit = async (values: NativeProblemFormValues) => {
    const id = uuidv4();
    const now = new Date().toISOString();
    const command: wasm.DatabaseCommand = {
      type: 'CreateItem',
      id,
      item: {
        type: 'Problem',
        value: {
          title: values.title,
          description: values.description || null,
          reported_by: null,
          resolved: false,
          resolved_at: null,
          created_at: now,
          updated_at: now,
        },
      },
    };
    await onSubmit(command);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Problem</h2>

        <Formik
          initialValues={{ title: '', description: '' }}
          validationSchema={toFormikValidationSchema(NativeProblemSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="title">Title</Label>
                </div>
                <Field
                  as={TextInput}
                  id="title"
                  name="title"
                  autoFocus
                  placeholder="e.g. Page load times are too slow"
                />
                <ErrorMessage name="title" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="description">Description</Label>
                </div>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  placeholder="What is the problem and why does it matter?"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Problem</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeProblemForm;
